import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { FileDown, Filter, Shield, AlertCircle } from 'lucide-react'
import { auditApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { formatDate } from '@/lib/utils'

const ACTION_COLORS: Record<string, string> = {
  LOGIN_GOOGLE: 'bg-verde text-white',
  LOGOUT: 'bg-gray-400 text-white',
  ASSESSMENT_CREATED: 'bg-mim-blue text-white',
  ASSESSMENT_COMPLETED: 'bg-verde text-white',
  ASSESSMENT_FAILED: 'bg-rosso text-white',
  ASSESSMENT_PROHIBITED_PRACTICE: 'bg-rosso-dark text-white',
  ASSESSMENT_REVIEW_APPROVED: 'bg-verde text-white',
  ASSESSMENT_REVIEW_OVERRIDDEN: 'bg-giallo-dark text-white',
}

export default function AuditLog() {
  const { t, i18n } = useTranslation()
  const { user } = useAuthStore()
  const [page, setPage] = useState(1)
  const [filterAction, setFilterAction] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const isAllowed = !!user && ['DIRIGENTE', 'DPO'].includes(user.role)

  const { data: logs, isLoading } = useQuery({
    queryKey: ['audit-logs', page, filterAction, dateFrom, dateTo],
    queryFn: () =>
      auditApi.list({
        page,
        page_size: 50,
        action: filterAction || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    enabled: isAllowed,
  })

  if (!isAllowed) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <p>{t('audit.accessRestricted')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const exportUrl = auditApi.exportCsvUrl({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  })

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-mim-blue" />
          <h1 className="text-2xl font-bold">{t('audit.title')}</h1>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href={exportUrl} download>
            <FileDown className="w-4 h-4 mr-2" />
            {t('audit.exportCsv')}
          </a>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">{t('audit.filterByAction')}:</Label>
              <Input
                className="h-8 text-sm w-36"
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                placeholder="es. LOGIN_GOOGLE"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Dal:</Label>
              <Input
                type="datetime-local"
                className="h-8 text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Al:</Label>
              <Input
                type="datetime-local"
                className="h-8 text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterAction('')
                setDateFrom('')
                setDateTo('')
                setPage(1)
              }}
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-semibold text-xs uppercase text-muted-foreground">
                    {t('audit.columns.timestamp')}
                  </th>
                  <th className="text-left py-3 pr-4 font-semibold text-xs uppercase text-muted-foreground">
                    {t('audit.columns.user')}
                  </th>
                  <th className="text-left py-3 pr-4 font-semibold text-xs uppercase text-muted-foreground">
                    {t('audit.columns.action')}
                  </th>
                  <th className="text-left py-3 pr-4 font-semibold text-xs uppercase text-muted-foreground">
                    {t('audit.columns.entity')}
                  </th>
                  <th className="text-left py-3 font-semibold text-xs uppercase text-muted-foreground">
                    {t('audit.columns.ip')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array(10).fill(0).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={5} className="py-2">
                        <Skeleton className="h-8 w-full" />
                      </td>
                    </tr>
                  ))
                ) : logs && logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(log.timestamp, i18n.language)}
                      </td>
                      <td className="py-3 pr-4">
                        <p className="text-xs font-medium">{log.user_name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{log.user_email || '—'}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge className={ACTION_COLORS[log.action] || 'bg-gray-400 text-white'}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {log.entity_type && (
                          <span className="text-muted-foreground">{log.entity_type} </span>
                        )}
                        {log.entity_id && (
                          <span className="font-mono text-xs">{log.entity_id.slice(0, 8)}…</span>
                        )}
                      </td>
                      <td className="py-3 text-xs text-muted-foreground font-mono">
                        {log.ip_address || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      Nessun log trovato
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {logs && logs.length === 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            ← {t('common.back')}
          </Button>
          <span className="text-sm text-muted-foreground flex items-center px-3">
            {t('common.page')} {page}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
            {t('common.next')} →
          </Button>
        </div>
      )}
    </div>
  )
}
